import { ChevronDownIcon } from '@moia-dev/pace-icons'
import { Text } from '@moia-dev/pace-web'
import type { ReactNode } from 'react'

type OverlaySectionProps = {
  title: string
  expanded: boolean
  onToggle: () => void
  children: ReactNode
  /** When true, the content area scrolls and the section grows to fill remaining panel height */
  scrollable?: boolean
  /** Optional search input rendered at the top of the content area when expanded */
  searchValue?: string
  searchPlaceholder?: string
  onSearchChange?: (value: string) => void
  /** Content rendered below the scroll area (outside the scroll container) */
  footer?: ReactNode
}

export const OverlaySection = ({
  title,
  expanded,
  onToggle,
  children,
  scrollable,
  searchValue,
  searchPlaceholder,
  onSearchChange,
  footer,
}: OverlaySectionProps) => (
  <div
    css={
      scrollable
        ? {
            flex: expanded ? 1 : '0 0 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }
        : undefined
    }
  >
    <button
      type="button"
      onClick={onToggle}
      css={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '6px 10px',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        flexShrink: 0,
        '&:hover': { opacity: 0.7 },
      }}
    >
      <Text weight="bold" size="sm">
        {title}
      </Text>
      <ChevronDownIcon
        css={{
          width: 16,
          height: 16,
          flexShrink: 0,
          transition: 'transform 200ms ease',
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
        }}
      />
    </button>

    {/* grid-template-rows trick works for both scrollable and non-scrollable sections.
        For scrollable: the grid wrapper is flex:1 so it fills the outer section height;
        grid-template-rows: 1fr then fills the grid's own height. */}
    <div
      css={{
        display: 'grid',
        gridTemplateRows: expanded ? '1fr' : '0fr',
        transition: 'grid-template-rows 200ms ease',
        ...(scrollable ? { flex: 1, minHeight: 0 } : {}),
      }}
    >
      <div
        css={{
          overflow: 'hidden',
          ...(scrollable
            ? { display: 'flex', flexDirection: 'column', minHeight: 0 }
            : {}),
        }}
      >
        <div
          css={{
            padding: '6px 10px',
            ...(scrollable
              ? {
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }
              : {}),
          }}
        >
          {onSearchChange && (
            <input
              type="text"
              value={searchValue ?? ''}
              onChange={e => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder ?? 'Search…'}
              css={theme => ({
                width: '100%',
                marginBottom: 6,
                padding: '4px 8px',
                border: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
                borderRadius: 4,
                fontSize: 12,
                outline: 'none',
                boxSizing: 'border-box',
                flexShrink: 0,
                '&:focus': {
                  borderColor: theme.semantic.ColorAccessoryDefault,
                },
              })}
            />
          )}
          <div
            css={
              scrollable
                ? {
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                  }
                : undefined
            }
          >
            {children}
          </div>
          {footer}
        </div>
      </div>
    </div>
  </div>
)
